import React, { useState, useEffect, useRef, useCallback } from 'react';
import squirrelImg from '../../assets/game/squirrel.png';
import carImg from '../../assets/game/car.png';
import logImg from '../../assets/game/log.png';
import acornImg from '../../assets/game/acorn.png';
import signImg from '../../assets/game/sign.png';

// Game Constants
const GRID_ROWS = 11;
const GRID_COLS = 11;
const INITIAL_LIVES = 3;

type Entity = {
  id: number;
  x: number;
  y: number;
  width: number;
  speed: number;
  type?: 'car' | 'log';
};

type GameState = 'start' | 'playing' | 'won' | 'gameover';

export const SquirrelCrossing: React.FC = () => {
  const [gameState, setGameState] = useState<GameState>('start');
  const [lives, setLives] = useState(INITIAL_LIVES);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [squirrel, setSquirrel] = useState({ x: 5, y: 9 }); // Start at Row 9
  const [cars, setCars] = useState<Entity[]>([]);
  const [logs, setLogs] = useState<Entity[]>([]);
  const [nuts, setNuts] = useState<{ id: number; x: number; y: number }[]>([]);
  
  const gameLoopRef = useRef<number>();
  const lastTimeRef = useRef<number>(0);

  const logsRef = useRef(logs);
  useEffect(() => { logsRef.current = logs; }, [logs]);

  // Initialize Level
  const initLevel = useCallback((currentLevel: number) => {
    setSquirrel({ x: 5, y: 9 });
    
    // Generate Cars (Rows 6, 7, 8)
    const newCars: Entity[] = [];
    [6, 7, 8].forEach((row, i) => {
      const speedMultiplier = 1 + (currentLevel - 1) * 0.15;
      const speed = (i % 2 === 0 ? 1 : -1) * (1 + i * 0.2) * speedMultiplier;
      
      // Increase car count every 2 levels, max 3
      let count = 1;
      if (currentLevel >= 3) count = 2;
      if (currentLevel >= 5) count = 3;

      const spacing = GRID_COLS / count;
      const offset = Math.random() * spacing;

      for (let j = 0; j < count; j++) {
        newCars.push({
          id: Math.random(),
          x: (j * spacing + offset) % GRID_COLS,
          y: row,
          width: 2.0, // Bigger cars
          speed: speed,
          type: 'car'
        });
      }
    });
    setCars(newCars);

    // Generate Logs (Rows 1, 2, 3, 4)
    const newLogs: Entity[] = [];
    [1, 2, 3, 4].forEach((row, i) => {
      const speedMultiplier = 1 + (currentLevel - 1) * 0.1;
      const speed = (i % 2 === 0 ? 1 : -1) * (0.8 + i * 0.2) * speedMultiplier;
      
      // Difficulty: Logs get smaller as levels increase
      const width = Math.max(1.5, 3.5 - (currentLevel - 1) * 0.2);
      
      // Increase log count with levels too
      let count = 1;
      if (currentLevel >= 4) count = 2;
      if (currentLevel >= 8) count = 3;

      const spacing = GRID_COLS / count;
      const offset = Math.random() * spacing;

      for (let j = 0; j < count; j++) {
        newLogs.push({
          id: Math.random(),
          x: (j * spacing + offset) % GRID_COLS,
          y: row,
          width: width, 
          speed: speed,
          type: 'log'
        });
      }
    });
    setLogs(newLogs);

    // Generate Nuts
    const newNuts = [];
    for (let i = 0; i < 5; i++) {
      newNuts.push({
        id: Math.random(),
        x: Math.floor(Math.random() * GRID_COLS),
        y: 5 // Middle safe zone (Row 5)
      });
    }
    setNuts(newNuts);
  }, []);

  const startGame = () => {
    setGameState('playing');
    setLives(INITIAL_LIVES);
    setScore(0);
    setLevel(1);
    initLevel(1);
  };

  // Movement Logic
  const moveSquirrel = useCallback((dx: number, dy: number) => {
    if (gameState !== 'playing') return;
    
    setSquirrel(prev => {
      const newX = Math.max(0, Math.min(GRID_COLS - 1, prev.x + dx));
      const newY = Math.max(0, Math.min(GRID_ROWS - 2, prev.y + dy)); // Can't go to Row 10 (Footer)
      return { x: newX, y: newY };
    });
  }, [gameState]);

  // Game Loop
  useEffect(() => {
    if (gameState !== 'playing') {
      if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
      return;
    }

    const update = (time: number) => {
      if (!lastTimeRef.current) lastTimeRef.current = time;
      const delta = (time - lastTimeRef.current) / 1000; // Seconds
      lastTimeRef.current = time;

      // Update Entities
      setCars(prev => prev.map(car => {
        let newX = car.x + car.speed * delta;
        if (newX > GRID_COLS) newX = -car.width;
        if (newX < -car.width) newX = GRID_COLS;
        return { ...car, x: newX };
      }));

      setLogs(prev => prev.map(log => {
        let newX = log.x + log.speed * delta;
        if (newX > GRID_COLS) newX = -log.width;
        if (newX < -log.width) newX = GRID_COLS;
        return { ...log, x: newX };
      }));

      // Move Squirrel on Log
      setSquirrel(prev => {
        if (prev.y >= 1 && prev.y <= 4) {
          const log = logsRef.current.find(l => 
            prev.y === l.y && 
            prev.x >= l.x - 1.2 && 
            prev.x <= l.x + l.width + 0.5
          );
          if (log) {
            let newX = prev.x + log.speed * delta;
            newX = Math.max(0, Math.min(GRID_COLS - 1, newX));
            return { ...prev, x: newX };
          }
        }
        return prev;
      });
      
      gameLoopRef.current = requestAnimationFrame(update);
    };

    gameLoopRef.current = requestAnimationFrame(update);
    return () => {
      if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
    };
  }, [gameState]);

  // Collision & Logic Check
  useEffect(() => {
    if (gameState !== 'playing') return;

    // Check Car Collision
    const carHit = cars.some(car => 
      squirrel.y === car.y && 
      squirrel.x >= car.x - 0.6 && 
      squirrel.x <= car.x + car.width - 0.4
    );

    if (carHit) {
      handleDeath();
      return;
    }

    // Check Water/Log Logic (Rows 1-4)
    if (squirrel.y >= 1 && squirrel.y <= 4) {
      const onLog = logs.some(log => 
        squirrel.y === log.y && 
        squirrel.x >= log.x - 1.2 && 
        squirrel.x <= log.x + log.width + 0.5
      );
      
      if (!onLog) {
        handleDeath(); // Drowned
        return;
      }
    }

    // Check Nut Pickup
    const nutIndex = nuts.findIndex(n => Math.abs(n.x - squirrel.x) < 0.5 && Math.abs(n.y - squirrel.y) < 0.5);
    if (nutIndex !== -1) {
      setNuts(prev => prev.filter((_, i) => i !== nutIndex));
      setScore(s => s + 100);
    }

    // Check Win (Reach top row 0)
    if (squirrel.y === 0) {
      setScore(s => s + 500);
      const nextLevel = level + 1;
      setLevel(nextLevel);
      initLevel(nextLevel); // Reset for next "level"
    }

  }, [squirrel, cars, logs, gameState, nuts, initLevel, level]);

  const handleDeath = () => {
    if (lives > 1) {
      setLives(l => l - 1);
      setSquirrel({ x: 5, y: 9 }); // Reset pos
    } else {
      setGameState('gameover');
    }
  };

  // Keyboard Controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch(e.key) {
        case 'ArrowUp': moveSquirrel(0, -1); break;
        case 'ArrowDown': moveSquirrel(0, 1); break;
        case 'ArrowLeft': moveSquirrel(-1, 0); break;
        case 'ArrowRight': moveSquirrel(1, 0); break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [moveSquirrel]);

  return (
    <div className="flex flex-col items-center justify-center h-[100dvh] bg-gray-900 text-white overflow-hidden touch-none select-none">
      
      {/* Header Row - Smaller */}
      <div className="w-full max-w-md h-12 bg-black flex items-center justify-center gap-2 border-b-4 border-yellow-500 z-20 shrink-0">
        <h1 className="text-lg font-bold text-yellow-400 uppercase tracking-wider whitespace-nowrap">Squirrel Crossing</h1>
        <div className="w-8 h-8 bg-yellow-400 flex items-center justify-center rounded-sm rotate-45 border-2 border-black overflow-hidden">
           <img src={signImg} className="w-full h-full object-contain -rotate-45 scale-125" alt="sign" />
        </div>
      </div>

      {/* Score Bar - Minimal Padding, Relaxed Leading */}
      <div className="w-full max-w-md flex justify-between px-4 py-1 bg-gray-800 z-10 shrink-0 items-center">
        <div className="text-base font-bold text-yellow-400">Score: {score}</div>
        <div className="text-base font-bold text-red-400 flex items-center gap-1">
          Lives: <span className="text-xl leading-relaxed">{'❤️'.repeat(lives)}</span>
        </div>
      </div>

      {/* Game Board - Fills Space */}
      <div 
        className="relative bg-green-600 overflow-hidden shadow-2xl flex-1 w-full max-w-md"
        style={{
          // Removed aspect-ratio to fill height
        }}
      >
        {/* Background Zones */}
        {/* Water (Rows 1-4) */}
        <div className="absolute top-[9.09%] h-[36.36%] w-full bg-blue-500 opacity-80 animate-pulse"></div>
        
        {/* Road (Rows 6-8) */}
        <div className="absolute top-[54.54%] h-[27.27%] w-full bg-gray-700 border-y-4 border-dashed border-white/20"></div>
        
        {/* Footer Zone (Row 10) */}
        <div className="absolute top-[90.90%] h-[9.09%] w-full bg-green-800 border-t-2 border-green-900 flex items-center justify-center">
           <span className="text-base text-yellow-400 font-bold">Level {level}</span>
        </div>

        {/* Entities */}
        
        {/* Logs */}
        {logs.map(log => (
          <img 
            key={log.id}
            src={logImg}
            alt="log"
            className="absolute h-[10%] object-contain"
            style={{
              left: `${(log.x / GRID_COLS) * 100}%`,
              top: `${(log.y / GRID_ROWS) * 100}%`,
              width: `${(log.width / GRID_COLS) * 100}%`,
            }}
          />
        ))}

        {/* Cars */}
        {cars.map(car => (
          <img 
            key={car.id}
            src={carImg}
            alt="car"
            className="absolute h-[10%] object-contain drop-shadow-lg"
            style={{
              left: `${(car.x / GRID_COLS) * 100}%`,
              top: `${(car.y / GRID_ROWS) * 100}%`,
              width: `${(car.width / GRID_COLS) * 100}%`,
              transform: car.speed < 0 ? 'scaleX(-1)' : 'none',
            }}
          />
        ))}

        {/* Nuts */}
        {nuts.map(nut => (
          <img 
            key={nut.id}
            src={acornImg}
            alt="nut"
            className="absolute w-[10%] h-[10%] object-contain animate-bounce"
            style={{
              left: `${(nut.x / GRID_COLS) * 100}%`,
              top: `${(nut.y / GRID_ROWS) * 100}%`,
              marginLeft: '0.5%',
              marginTop: '0%'
            }}
          />
        ))}

        {/* Squirrel */}
        <img 
          src={squirrelImg}
          alt="squirrel"
          className="absolute w-[14%] h-[14%] object-contain z-20 transition-all duration-100"
          style={{
            left: `${(squirrel.x / GRID_COLS) * 100}%`,
            top: `${(squirrel.y / GRID_ROWS) * 100}%`,
            marginTop: '-2%'
          }}
        />

        {/* Overlays */}
        {gameState === 'start' && (
          <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center z-50">
            <h1 className="text-4xl font-bold text-yellow-400 mb-4 text-center">Ready?</h1>
            <button 
              onClick={startGame}
              className="px-8 py-4 bg-green-500 text-white text-2xl font-bold rounded-xl shadow-lg hover:bg-green-600 active:scale-95 transition-transform"
            >
              Play
            </button>
          </div>
        )}

        {gameState === 'gameover' && (
          <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center z-50">
            <h2 className="text-4xl font-bold text-red-500 mb-2">Game Over</h2>
            <p className="text-2xl text-white mb-6">Score: {score}</p>
            <button 
              onClick={startGame}
              className="px-8 py-4 bg-blue-500 text-white text-2xl font-bold rounded-xl shadow-lg hover:bg-blue-600 active:scale-95 transition-transform"
            >
              Try Again
            </button>
          </div>
        )}
      </div>

      {/* Mobile Controls */}
      <div className="w-full max-w-md grid grid-cols-3 gap-2 p-4 mt-auto mb-4 shrink-0">
        <div className="col-start-2">
          <button 
            className="w-full h-16 bg-gray-700/50 rounded-xl active:bg-gray-600 flex items-center justify-center text-3xl"
            onPointerDown={(e) => { e.preventDefault(); moveSquirrel(0, -1); }}
          >
            ⬆️
          </button>
        </div>
        <div className="col-start-1 row-start-2">
          <button 
            className="w-full h-16 bg-gray-700/50 rounded-xl active:bg-gray-600 flex items-center justify-center text-3xl"
            onPointerDown={(e) => { e.preventDefault(); moveSquirrel(-1, 0); }}
          >
            ⬅️
          </button>
        </div>
        <div className="col-start-2 row-start-2">
          <button 
            className="w-full h-16 bg-gray-700/50 rounded-xl active:bg-gray-600 flex items-center justify-center text-3xl"
            onPointerDown={(e) => { e.preventDefault(); moveSquirrel(0, 1); }}
          >
            ⬇️
          </button>
        </div>
        <div className="col-start-3 row-start-2">
          <button 
            className="w-full h-16 bg-gray-700/50 rounded-xl active:bg-gray-600 flex items-center justify-center text-3xl"
            onPointerDown={(e) => { e.preventDefault(); moveSquirrel(1, 0); }}
          >
            ➡️
          </button>
        </div>
      </div>

    </div>
  );
};
